import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { postNewProjectTemplate } from '../../../../api/Sherpa/ProjectTemplate';
import type { PostNewProjectTemplatePayload } from '../../../../api/Sherpa/ProjectTemplate/types';
import { constructionType, projectType } from '../../../../constants/Project';
import { SherpaProjectReactQueryKey } from '../../../../constants/ReactQueryKeys';
import { notifyUser } from '../../../../store/Toast';
import { sanitizeResponseError } from '../../../../utils';
import type { NewProjectTemplateInterface } from '../../types';
import { validationSchema } from './validationSchema';

interface NewProjectTemplateProps {
  handleModal: () => void;
}

const projectTemplateDefaultValues = {
  name: '',
  projectType: '',
  constructionType: '',
  description: '',
};

const NewProjectTemplateBase = ({ handleModal }: NewProjectTemplateProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const descriptionValues = useWatch({ name: 'description' });

  const addProjectTemplate = useMutation((data: PostNewProjectTemplatePayload) => postNewProjectTemplate({ data }), {
    onSuccess: () => {
      queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_TEMPLATES]);
      handleModal();
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Template created successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const submitNewProjectTemplate = (data: NewProjectTemplateInterface) => {
    const transformedData: PostNewProjectTemplatePayload = {
      ...data,
      constructionType: data.constructionType.value,
      projectType: data.projectType.value,
    };
    addProjectTemplate.mutate(transformedData);
  };

  return (
    <HookForm onSubmit={submitNewProjectTemplate} className='space-y-5'>
      <HookInput type='text' label='Template Name' name='name' required />
      <HookReactSingleSelect label='Construction Type' name='constructionType' required options={constructionType} />
      <HookReactSingleSelect label='Project Type' name='projectType' required options={projectType} />
      <HookTextarea name='description' label='Description (Optional)' maxLength={250} />
      <span className='text-xs'>{descriptionValues.length}/250 characters</span>
      <div className='flex gap-4 mt-5 justify-end'>
        <Button label='Cancel' type='button' btnStyle='btn-neutral' onClick={handleModal} />
        <Button
          label={`${addProjectTemplate.isLoading ? 'Adding...' : 'Add'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={addProjectTemplate.isLoading}
        />
      </div>
    </HookForm>
  );
};

export const NewProjectTemplate = (props: NewProjectTemplateProps) => {
  return (
    <Modal>
      <h2 className='mt-0'>New Template</h2>
      <HookFormProvider defaultValues={projectTemplateDefaultValues} resolver={yupResolver(validationSchema) as any}>
        <NewProjectTemplateBase {...props} />
      </HookFormProvider>
    </Modal>
  );
};
